/**
 * The headers every request to `/openai/chatkit/` carries.
 *
 * One place, because the embedded chat and the agent chat page had drifted: the
 * hand-rolled embedded chat sent `pymble-impersonate` and ChatKitPanel never
 * did, which is invisible until a staff critter answers about the wrong person.
 *
 * NOTHING HERE IS AUTHORISATION. The API loads the agent's and the context's own
 * settings and decides what the named agent may do for this caller. These headers
 * say who is asking and what about; they do not grant anything.
 */

/**
 * Marks an `X-User-Context` value as base64url-encoded UTF-8 JSON.
 *
 * THE WIRE CONTRACT, both halves:
 *   - value starts with `b64:` -> the remainder is base64url (RFC 4648 §5,
 *     `-` and `_` for the last two characters, PADDING KEPT) of the UTF-8
 *     bytes of the JSON document.
 *   - value does not start with `b64:` -> legacy raw JSON, as sent by bundles
 *     built before this change. The API keeps that fallback for the rollout
 *     and can drop it once no old bundle is in circulation.
 */
const USER_CONTEXT_B64_PREFIX = "b64:";

/**
 * UTF-8 JSON as base64url, so the value survives `Headers.set`.
 *
 * `JSON.stringify` happily returns any code point, but a header value is
 * Latin-1: `Headers.set` throws on anything above U+00FF, and the characters
 * that do fit above U+007F go out mojibaked. Sending a student's name in this
 * header therefore broke the widget outright for Chinese, Japanese and Korean
 * names - and for a name carrying a curly apostrophe - while é and ü arrived
 * corrupted. Base64 makes the value pure ASCII, so every name transports
 * byte-exactly.
 *
 * TextEncoder gives the UTF-8 bytes; `btoa` needs a binary string, and the
 * bytes are walked in chunks because `String.fromCharCode(...bytes)` spreads
 * the whole array onto the call stack and blows up on a large context.
 */
export function encodeUserContext(userContext: Record<string, unknown>): string {
  const bytes = new TextEncoder().encode(JSON.stringify(userContext));
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const b64 = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_");
  return `${USER_CONTEXT_B64_PREFIX}${b64}`;
}

export interface ChatKitHeaderInput {
  /** MSAL access token for the S2 API. */
  accessToken: string;
  agentId: string;
  agentName?: string;
  agentModel?: string;
  /**
   * `{ context, contextId, help_mode_id }` and friends.
   *
   * Values are PII (a selected student, a contact id). Never log them - log
   * `Object.keys()` if you must log anything at all.
   */
  userContext?: Record<string, unknown>;
  impersonateContactId?: string | null;
}

export function chatKitHeaders({
  accessToken,
  agentId,
  agentName,
  agentModel,
  userContext,
  impersonateContactId,
}: ChatKitHeaderInput): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
    "X-ChatKit-Config": JSON.stringify({
      id: agentId,
      // Truthiness, not presence. An embedded surface knows only an agent id, so
      // sending `model: ""` would blank the agent's configured model server-side.
      ...(agentName ? { name: agentName } : {}),
      ...(agentModel ? { model: agentModel } : {}),
    }),
    // Empty rather than absent when nobody is impersonated, matching what the
    // hand-rolled chat sent so the API sees no change in shape.
    "pymble-impersonate": impersonateContactId ?? "",
  };

  if (userContext && Object.keys(userContext).length > 0) {
    headers["X-User-Context"] = encodeUserContext(userContext);
  }

  return headers;
}

/**
 * Stamp the built headers onto a request's headers.
 *
 * `chatKitHeaders` always names `application/json`, and must keep doing so:
 * fetch would otherwise default a string body to `text/plain`. A FormData body
 * is the exception. The browser writes its own
 * `multipart/form-data; boundary=----WebKitFormBoundary...` ONLY if the header
 * is left unset, and `set`ting JSON over it strips the boundary with it - so
 * the body goes out genuinely multipart while the header claims JSON, Starlette
 * parses an empty form, and FastAPI reports `body.file` missing with
 * `input: null`. That is the ChatKit direct file upload, on both surfaces.
 *
 * The decision lives here rather than in `chatKitHeaders` because the builder
 * cannot see the body, and here rather than at the two call sites because this
 * module exists to stop those two surfaces drifting - and because ChatKitPanel
 * cannot be imported under vitest, so a copy living there could not be tested.
 *
 * Only the content type changes: `X-ChatKit-Config`, `X-User-Context` and
 * `pymble-impersonate` all still go out on an upload, which is what says who is
 * asking and about whom.
 */
export function applyChatKitHeaders(
  headers: Headers,
  built: Record<string, string>,
  body: RequestInit["body"]
): void {
  Object.entries(built).forEach(([key, value]) => headers.set(key, value));
  // Deletes a caller-supplied value too, deliberately: a `Content-Type` named
  // by the call site breaks a multipart body exactly the same way ours does.
  if (body instanceof FormData) headers.delete("Content-Type");
}
